import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Filter, FilterSelection, GroupWrapper } from '../Filters.style';
import { FilterOption } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import FilterExposedHeader from '../FilterExposedHeader';
import {
  ShowAllButton,
  ModelFamilyStyleFrame,
  AllModelsWrapper,
  Legend,
  CTAWrapper,
  CommonModelsTitle,
} from './ModelFamilySelector.style';
import Search from './Search/Search';
import ActiveModelFamiliesList from './ActiveModelFamilies';
import CheckboxSelector from '../../CheckboxComponent';
import useAnimations from '../../../hooks/Animations/useAnimations';
import CommonModelsList from './CommonModels';
import { CommonModelFamiliesValues, CommonModelFamiliesValuesUS } from './modelFamiliesConfigs';
import { selectZipCode } from '../../../reducers/zipCodeReducer';

interface ModelFamilySelectorTypes {
  filterType: string;
  modelFamilyOptions: {
    [key: string]: FilterOption;
  };
  confirmedFilters: FilterTypes;
  availableFilters: string[];
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string, isChecked?: boolean) => void;
  handleExposeFilter: (arg: string) => void;
}

const ModelFamilySelector: React.FC<ModelFamilySelectorTypes> = ({
  filterType,
  modelFamilyOptions,
  confirmedFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
  availableFilters,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  // Animation
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [showAll]);

  const selectedModelFamilyLabels = useMemo(() => {
    const seletedModelFamilyList = confirmedFilters.modelFamily;
    return seletedModelFamilyList.map((body) => modelFamilyOptions[body].label).join(', ');
  }, [modelFamilyOptions, confirmedFilters.modelFamily]);

  const selectedModelFamilyFilters = useMemo(() => {
    return confirmedFilters.modelFamily;
  }, [confirmedFilters.modelFamily]);

  const groupedModelFamilies = useMemo(() => {
    // Filter options with defined tabs
    const allOptions = Object.values(modelFamilyOptions);

    // Group options by tabs using reduce
    const groupedByTabs = allOptions.reduce(
      (acc, item) => {
        item.tabs?.forEach((tab) => {
          acc[tab] = acc[tab] || [];
          acc[tab].push(item);
        });
        return acc;
      },
      {} as { [key: string]: FilterOption[] },
    );

    return groupedByTabs;
  }, [modelFamilyOptions]);

  const commonModels = isCaMarket ? CommonModelFamiliesValues : CommonModelFamiliesValuesUS;

  const commmonModelFamilyOptions = useMemo(() => {
    const commonModelFamiliesSet = new Set(commonModels);

    const filteredOptions = Object.fromEntries(
      Object.entries(modelFamilyOptions).filter(([, value]) =>
        commonModelFamiliesSet.has(value.value),
      ),
    );

    // Soriting the object, so it is foloowing initial order from array
    const sortedOptions = Object.fromEntries(
      Object.entries(filteredOptions).sort(([keyA], [keyB]) => {
        return commonModels.indexOf(keyA) - commonModels.indexOf(keyB);
      }),
    );

    return sortedOptions;
  }, [commonModels, modelFamilyOptions]);

  const handleAddModelFamily = (newModelFamily: string, isChecked?: boolean) => {
    handleAddFilter(filterType, newModelFamily, isChecked);
  };

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  const hasAnyFilterSelected = !!selectedModelFamilyFilters.length;

  return (
    <Filter>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedModelFamilyLabels}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props}>
              <ModelFamilyStyleFrame style={filtersAccordianAnimation}>
                <Search
                  allOptions={Object.keys(modelFamilyOptions)}
                  modelFamilyOptions={modelFamilyOptions}
                  onChange={handleAddModelFamily}
                />
                <ActiveModelFamiliesList
                  modelFamilyOptions={modelFamilyOptions}
                  selectedModelFamilyFilters={selectedModelFamilyFilters}
                  onChange={handleAddModelFamily}
                />
                <CommonModelsTitle hasAnyFilterSelected={hasAnyFilterSelected}>
                  {t(`promo.filters.filterOptions.modelFamily.modelsNote`)}
                </CommonModelsTitle>
                <CommonModelsList
                  commonModelFamilyOptions={commmonModelFamilyOptions}
                  selectedModelFamilyFilters={selectedModelFamilyFilters}
                  availableFilters={availableFilters}
                  onChange={handleAddModelFamily}
                  filterType={filterType}
                />
                <CTAWrapper
                  expanded={showAll}
                  hasMarginTop={selectedModelFamilyFilters?.length > 0}
                >
                  <ShowAllButton onClick={() => setShowAll(!showAll)}>
                    {showAll
                      ? t(`promo.filters.filterOptions.modelFamily.showFewer`)
                      : t(`promo.filters.filterOptions.modelFamily.showAll`)}
                  </ShowAllButton>
                </CTAWrapper>
                <AllModelsWrapper
                  expanded={showAll}
                  ref={contentRef}
                  contentHeight={`${contentHeight}px` || '0'}
                >
                  {/* Temporary fix */}
                  {showAll && (
                    <>
                      {Object.keys(groupedModelFamilies).map((group) => {
                        return (
                          <div key={group}>
                            <Legend>{t(`promo.filters.filterOptions.modelFamily.${group}`)}</Legend>
                            <GroupWrapper>
                              {groupedModelFamilies[group].map((model) => {
                                return (
                                  <CheckboxSelector
                                    inputName={`${group}-${model.value}`}
                                    key={model.value}
                                    onChange={() =>
                                      handleAddModelFamily(
                                        model.value,
                                        selectedModelFamilyFilters.includes(model.value),
                                      )
                                    }
                                    value={model.value}
                                    label={model.label}
                                    ariaLabel={model.ariaLabel}
                                    checked={selectedModelFamilyFilters.includes(model.value)}
                                    disabled={!availableFilters.includes(model.value)}
                                    filterType={filterType}
                                  />
                                );
                              })}
                            </GroupWrapper>
                          </div>
                        );
                      })}
                    </>
                  )}
                </AllModelsWrapper>
              </ModelFamilyStyleFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default ModelFamilySelector;
